import { Link, NavLink } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandWordmark } from "@/components/BrandWordmark";
import Seo from "@/components/Seo";

const tabs = [
  { to: "/terms", label: "Terms of Service" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/acceptable-use", label: "Acceptable Use" },
  { to: "/legal", label: "Content & Copyright" },
];

export default function LegalLayout({ title, updated = "June 2026", intro, children, testid = "legal-layout", path = "" }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid={testid}>
      <Seo title={title} description={intro ? String(intro).slice(0, 155) : `${title} for Videoileo, the AI faceless video studio.`} path={path} />
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandWordmark className="h-12" />
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 py-12">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Legal</p>
        <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight mb-3">{title}</h1>
        <p className="text-zinc-400 mb-6">Last updated: {updated}.</p>

        <div className="flex flex-wrap gap-2 mb-10 border-b border-white/10 pb-4">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) =>
                `text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  isActive ? "border-blue-500 bg-blue-600/15 text-white" : "border-white/10 text-zinc-400 hover:text-white"
                }`
              }
            >
              {t.label}
            </NavLink>
          ))}
        </div>

        {intro && <p className="text-zinc-400 mb-8 leading-relaxed">{intro}</p>}
        {children}

        <p className="text-xs text-zinc-600 mt-12 border-t border-white/10 pt-6 leading-relaxed">
          Questions? <Link to="/contact" className="text-zinc-500 hover:text-zinc-300 underline" data-testid="legal-contact-link">Contact us</Link> or email{" "}
          <a href="mailto:support@videoileo.com" className="text-zinc-500 hover:text-zinc-300 underline">
            support@videoileo.com
          </a>
          . This document is provided for general informational purposes and does not constitute legal advice.
        </p>
      </div>
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-head text-lg font-semibold mb-2">{title}</h2>
      <div className="text-sm text-zinc-400 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}
